import { electronApis } from '@/features/electron-api';

import type { AppWindowInfo } from '@electron/main/ipcBridge/ipcInboundPayloads';

export type CapturableWindow = AppWindowInfo;

/** Screen capture API wrapping macOS screen recording. */
export class Screenshot {
  /** Returns all visible windows eligible for capture. */
  static async listWindows() {
    return electronApis.context.getCapturableWindows();
  }

  /** Whether the app has macOS screen recording permission. */
  static async checkPermission() {
    return electronApis.context.checkScreenRecordingPermission();
  }

  /** Opens the macOS screen recording permission prompt. Requires app restart to take effect. */
  static async requestPermission() {
    await electronApis.context.requestScreenRecordingPermission();
  }

  /** Captures a window as a PNG File. */
  static async capture(windowId: number, fileName?: string) {
    const screenshot =
      await electronApis.context.captureAppScreenshot(windowId);

    if (screenshot.error || !screenshot.imageData)
      throw new Error(screenshot.error || 'Failed to capture screenshot');

    const byteString = atob(screenshot.imageData);
    const uint8Array = new Uint8Array(byteString.length);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([uint8Array], { type: 'image/png' });
    const safeName = (fileName || 'screenshot').replace(/[^a-zA-Z0-9]/g, '_');

    return new File([blob], `${safeName}.png`, { type: 'image/png' });
  }
}
